import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import animationData from "../../../../jsons/four-rotated-arrow.json";
import "react-image-crop/dist/ReactCrop.css";
import { useDebounceEffect } from "./useDebounceEffect";
import { canvasPreview } from "./canvasPreview";
import ReactCrop, { centerCrop, makeAspectCrop, Crop } from "react-image-crop";
import Lottie from "react-lottie";
import ButtonComponent from "../../../elements/ButtonComponent";

const EditImagePopup: React.FC<any> = ({
  onClose,
  setSelectedImage,
  name,
  isFile,
  minWidthCrop = 150,
  minHeightCrop = 120,
  setValue,
  imageData,
  handleChequeImageChange,
}) => {
  const [crop, setCrop] = useState<Crop>();
  const blobUrlRef = useRef("");
  const [completedCrop, setCompletedCrop] = useState<any>();
  const [rotate, setRotate] = useState(0);
  const [scale, setScale] = useState(1);
  const [aspect, setAspect] = useState<number>(16 / 9);
  const [imageUrl, setImageUrl] = useState<any>(undefined);
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number
  ) {
    const maxWidth = Math.min(mediaWidth, window.innerWidth * 0.8);
    const maxHeight = Math.min(mediaHeight, window.innerHeight * 0.8);

    return centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        aspect,
        maxWidth,
        maxHeight
      ),
      maxWidth,
      maxHeight
    );
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate
        );
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  const convertBlobToBinary = async (blob: any) => {
    const file = new File([blob], "file.png", { type: blob.type });
    if (isFile) {
      setValue(name, file);
      let objectUrl = URL.createObjectURL(file);
      handleChequeImageChange(objectUrl);
    } else handleChequeImageChange(file);
  };

  async function onDownloadCropClick() {
    const image = imgRef.current;

    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error("Crop canvas does not exist");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const offscreen = new OffscreenCanvas(
      completedCrop.width * scaleX,
      completedCrop.height * scaleY
    );
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height
    );

    const blob = await offscreen.convertToBlob({
      type: "image/jpeg",
      quality: 0.7,
    });
    convertBlobToBinary(blob);
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    blobUrlRef.current = URL.createObjectURL(blob);
    setSelectedImage(blobUrlRef.current);
    onClose("justClose");
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  const defaultOptions = {
    autoplay: true,
    loop: true,
    animationData: animationData,
    renderer: "svg",
  };

  useEffect(() => {
    if (imageData && isFile) {
      const objectUrl = URL.createObjectURL(imageData);
      setImageUrl(objectUrl);
    }
  }, [imageData]);

  useEffect(() => {
    const handleResize = () => {
      if (imgRef.current) {
        const { width, height } = imgRef.current;
        setCrop(centerAspectCrop(width, height, aspect));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [aspect]);

  return ReactDOM.createPortal(
    <>
      <div
        id="static-modal"
        data-modal-backdrop="static"
        tabIndex={-1}
        aria-hidden="true"
        className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center h-[100%] bg-gray-500 bg-opacity-50"
      >
        <div
          className={`relative bg-white w-full max-w-[80%]  max-h-[90%] rounded-lg shadow pb-4 px-4 overflow-auto`}
        >
          <div className="relative">
            <div dir="ltr" className="flex flex-col gap-2 w-full">
              <div className="flex items-end justify-end rounded-t pt-2">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                  }}
                  className="text-gray-400 bg-transparent rounded-lg w-8 h-8 inline-flex justify-start items-center"
                >
                  <img src="/icons/x.svg" alt="icon" width={20} />
                  <span className="sr-only">Close modal</span>
                </button>
              </div>

              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c: any) => setCompletedCrop(c)}
                minWidth={minWidthCrop}
                minHeight={minHeightCrop}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={isFile ? imageUrl : imageData}
                  style={{
                    
                    transform: `rotate(${rotate}deg)`,
                    maxWidth: "100%",
                    maxHeight: "calc(90vh - 100px)", // Adjust based on modal padding
                    objectFit: "contain",
                  }}
                  onLoad={onImageLoad}
                  className="rounded-md"
                />
              </ReactCrop>


              {!!completedCrop && (
                <div className="hidden">
                  <canvas
                    ref={previewCanvasRef}
                    style={{
                      border: "1px solid black",
                      objectFit: "contain",
                      width: completedCrop.width,
                      height: completedCrop.height,
                    }}
                  />
                </div>
              )}
              <div className="flex gap-4 justify-center items-center w-full">
                <div
                  className="flex hover:bg-primary-50 rounded-lg justify-center items-center gap-2 border p-1.5 border-primary-300 cursor-pointer"
                  onClick={() =>
                    setRotate((prev: any) => {
                      if (prev > 180) {
                        return 0;
                      } else {
                        return prev + 90;
                      }
                    })
                  }
                >
                  <p className="text-gray-700 text-BodyText/14/Regular">
                    چرخاندن
                  </p>
                  <Lottie options={defaultOptions} height={25} />
                </div>
                <ButtonComponent
                  onClick={() => {
                    onDownloadCropClick();
                  }}
                >
                  برش و ثبت
                </ButtonComponent>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.getElementById("modal-root")!
  );
};

export default EditImagePopup;
